import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  Button,
  Text,
  tokens,
  makeStyles,
  Textarea,
  Spinner,
  Card,
  CardHeader,
  Badge,
} from '@fluentui/react-components';
import {
  Send20Regular,
  ChatMultiple20Regular,
  ClipboardBulletList20Regular,
  Doctor20Regular,
  ChevronDown20Regular,
  ChevronRight20Regular,
  DocumentMultiple20Regular,
  Sparkle20Regular,
} from '@fluentui/react-icons';
import { ChatMessage, ChatState, GradeData, TodoItem } from '../models/types';
import { ExtractedLink, LinkExtractor } from '../utils/linkExtractor';
import { ChatAPI } from '../utils/api';
import { retryWithBackoff, RetryController } from '../utils/retryWithBackoff';
import { MessageRenderer } from './MessageRenderer.js';
import { UserProfile } from './UserProfile';
import { useTheme } from '../utils/theme';
import { InlineGradeBadge } from './InlineGradeBadge';
import { ToolContentModal } from './ToolContentModal';
import TodoPanel from './TodoPanel';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: 'hidden',
    position: 'relative',
  },
  messagesContainer: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'visible',
    padding: '16px 28px',
    paddingBottom: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    position: 'relative',
    scrollBehavior: 'smooth',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  messageCard: {
    maxWidth: '76%',
    minHeight: 'auto',
    height: 'auto',
    flexShrink: 0,
    position: 'relative',
    overflow: 'visible',
  },
  agentMessageWrapper: {
    alignSelf: 'stretch',
    flexShrink: 0,
    position: 'relative',
    overflow: 'visible',
    paddingBottom: '20px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  agentMessageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    paddingBottom: '8px',
  },
  agentIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: '6px',
    background: 'linear-gradient(135deg, #0EA5E9 0%, #2563EB 100%)',
    color: '#FFFFFF',
    flexShrink: 0,
    fontSize: '14px',
    fontWeight: 700,
    fontFamily: "'Syne', system-ui, sans-serif",
    letterSpacing: '-0.02em',
  },
  agentName: {
    fontFamily: "'Syne', system-ui, sans-serif",
    fontWeight: 700,
    fontSize: '13px',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: tokens.colorNeutralForeground1,
  },
  messageHeader: {
    padding: tokens.spacingVerticalS,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  messageContent: {
    padding: tokens.spacingVerticalM,
    fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif",
    fontSize: '14.5px',
    lineHeight: '1.8',
    wordBreak: 'break-word',
    minHeight: 'auto',
    height: 'auto',
    overflow: 'visible',
  },
  inputContainer: {
    padding: '10px 16px',
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    display: 'flex',
    gap: '10px',
    alignItems: 'flex-end',
    flexShrink: 0,
    boxShadow: `0 -1px 0 ${tokens.colorNeutralStroke1}`,
  },
  inputArea: {
    flex: 1,
    minHeight: '52px',
    maxHeight: '160px',
  },
  sendButton: {
    minWidth: '40px',
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    flexShrink: 0,
    marginBottom: '2px',
    background: 'linear-gradient(135deg, #0EA5E9 0%, #2563EB 100%)',
    border: 'none',
  },
  emptyState: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground2,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: tokens.colorNeutralBackground2,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
    backdropFilter: 'blur(4px)',
    gap: tokens.spacingVerticalM,
  },
  streamingIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 14px',
    backgroundColor: tokens.colorBrandBackground2,
    border: `1px solid ${tokens.colorBrandStroke2}`,
    borderRadius: '999px',
    fontSize: '12px',
    color: tokens.colorBrandForeground1,
    alignSelf: 'flex-start',
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    fontWeight: 500,
  },
  suggestionContainer: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    marginTop: tokens.spacingVerticalL,
    width: '100%',
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
  },
  bubbleCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    color: tokens.colorNeutralForeground1,
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}`,
    borderRadius: '12px',
    boxShadow: '0 1px 4px rgba(15,23,42,0.06)',
    cursor: 'pointer',
    minWidth: '260px',
    maxWidth: '360px',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
    ':hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 24px rgba(37,99,235,0.12)',
      border: `1px solid ${tokens.colorBrandStroke2}`,
    },
  },
  bubbleCardTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  suggestionBar: {
    display: 'flex',
    gap: '8px',
    padding: '6px 16px',
    alignItems: 'center',
    overflowX: 'auto',
    backgroundColor: tokens.colorNeutralBackground2,
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  bubble: {
    flex: '0 0 auto',
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground2,
    padding: '5px 14px',
    borderRadius: '999px',
    cursor: 'pointer',
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    fontSize: '12px',
    fontWeight: 500,
    transition: 'transform 0.12s ease, box-shadow 0.12s ease, background-color 0.12s ease, border-color 0.12s ease',
    ':hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 2px 8px rgba(37,99,235,0.12)',
      backgroundColor: tokens.colorBrandBackground2,
      border: `1px solid ${tokens.colorBrandStroke2}`,
      color: tokens.colorBrandForeground1,
    },
    ':active': {
      transform: 'translateY(0)',
    },
  },
  bubblePrimary: {
    background: '#0EA5E9',
    color: '#FFFFFF',
    border: 'none',
    fontWeight: 600,
  },
  bubbleSmall: {
    padding: '4px 12px',
    fontSize: '12px',
  },
  scrollToBottomButton: {
    position: 'absolute',
    bottom: '130px',
    right: tokens.spacingHorizontalL,
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    zIndex: 10,
    boxShadow: '0 4px 16px rgba(37,99,235,0.25)',
    transition: 'all 0.2s ease-in-out',
    opacity: 0.9,
    background: 'linear-gradient(135deg, #0EA5E9 0%, #2563EB 100%)',
    border: 'none',
    ':hover': {
      opacity: 1,
      transform: 'scale(1.08)',
      boxShadow: '0 8px 24px rgba(37,99,235,0.35)',
    },
  },
});

interface ChatInterfaceProps {
  chatState: ChatState;
  inputValue: string;
  streamingMessage: string;
  statusMessage: string;
  isLoadingConversation: boolean;
  loadingConversationError: string | null;
  gradeData: GradeData[]; // Array of all grades for this conversation
  selectedDocuments: string[]; // Array of selected document GUIDs
  uiMode?: string; // 'Authorizations' or 'Appeals' from segmented control
  todos: TodoItem[]; // Live agent todo list
  onTodosChange: (todos: TodoItem[]) => void; // Updates todo state in ChatContainer
  onChatStateChange: (updater: (prev: ChatState) => ChatState) => void;
  onInputValueChange: (value: string) => void;
  onStreamingMessageChange: (message: string) => void;
  onStatusMessageChange: (message: string) => void;
  onConversationLinksChange: (links: ExtractedLink[]) => void;
  onLoadDocuments: (authId: string) => Promise<void>;
  onGradeReceived?: (gradeData: any) => void;
  onRefreshGrades?: (threadId: string, turnCorrelationId?: string) => Promise<GradeData[]>;
}

/** Purple accent values for the thinking/reasoning block (dark + light variants) */
const THINKING_COLORS = {
  borderLight: '#E9D5FF',
  borderDark: '#4a2080',
  headerBgLight: '#F5F3FF',
  headerBgDark: '#2d1b50',
  bodyBgLight: '#FAFAFE',
} as const;

/** Collapsible "Model Reasoning" block rendered above a message during/after streaming */
const ThinkingBlock: React.FC<{ content: string; defaultExpanded?: boolean; label?: string }> = ({
  content,
  defaultExpanded = false,
  label = 'Model Reasoning',
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const { currentTheme } = useTheme();
  const thinkingBorder = currentTheme.isDark ? THINKING_COLORS.borderDark : THINKING_COLORS.borderLight;
  const thinkingHeaderBg = currentTheme.isDark ? THINKING_COLORS.headerBgDark : THINKING_COLORS.headerBgLight;
  const thinkingBodyBg = currentTheme.isDark ? tokens.colorNeutralBackground3 : THINKING_COLORS.bodyBgLight;
  return (
    <div style={{ marginBottom: '8px', border: `1px solid ${thinkingBorder}`, borderRadius: '8px', overflow: 'hidden' }}>
      <div
        onClick={() => setIsExpanded(prev => !prev)}
        style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          padding: '6px 12px', cursor: 'pointer', backgroundColor: thinkingHeaderBg,
          userSelect: 'none',
        }}
      >
        <Sparkle20Regular style={{ fontSize: '14px', color: '#7C3AED', flexShrink: 0 }} />
        <Text size={200} style={{ color: '#7C3AED', fontWeight: 600 }}>{label}</Text>
        <span style={{ flex: 1 }} />
        {isExpanded
          ? <ChevronDown20Regular style={{ fontSize: '14px', color: tokens.colorNeutralForeground3 }} />
          : <ChevronRight20Regular style={{ fontSize: '14px', color: tokens.colorNeutralForeground3 }} />}
      </div>
      {isExpanded && (
        <div style={{
          padding: '10px 12px', maxHeight: '220px', overflowY: 'auto',
          backgroundColor: thinkingBodyBg, borderTop: `1px solid ${thinkingBorder}`,
          fontSize: '13px', color: tokens.colorNeutralForeground2, lineHeight: '1.6',
          whiteSpace: 'pre-wrap',
        }}>
          {content}
        </div>
      )}
    </div>
  );
};

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  chatState,
  inputValue,
  streamingMessage,
  statusMessage,
  isLoadingConversation,
  loadingConversationError,
  gradeData,
  selectedDocuments,
  uiMode = 'Authorizations',
  todos,
  onTodosChange,
  onChatStateChange,
  onInputValueChange,
  onStreamingMessageChange,
  onStatusMessageChange,
  onConversationLinksChange,
  onGradeReceived,
  onRefreshGrades,
}) => {
  const classes = useStyles();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const streamingStartRef = useRef<HTMLDivElement>(null);
  const hasScrolledToStreamStart = useRef(false);
  const activeRetryControllers = useRef<Map<string, RetryController>>(new Map());
  const currentSessionRef = useRef<string | null>(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [currentToolContent, setCurrentToolContent] = useState<any[] | null>(null);
  const [isToolModalOpen, setIsToolModalOpen] = useState(false);
  const [streamingThinking, setStreamingThinking] = useState('');

  // Function to associate grades with messages
  // Grades are typically associated with assistant messages based on timing and sequence
  const associateGradesWithMessages = useCallback((messages: ChatMessage[], grades: GradeData[]): ChatMessage[] => {
    if (!grades.length) return messages;

    // Get only assistant messages with timestamps for grading association
    const assistantMessages = messages
      .map((msg, index) => ({ ...msg, originalIndex: index }))
      .filter(msg => msg.role === 'assistant' && msg.timestamp);

    // Sort grades by timestamp to match chronological order
    const sortedGrades = [...grades].sort((a, b) => a.timestamp - b.timestamp);

    // Create a copy of messages to avoid mutation
    const messagesWithGrades = messages.map(msg => ({ ...msg }));

    // Associate grades with assistant messages based on chronological order
    sortedGrades.forEach((grade, gradeIndex) => {
      // For each grade, find the corresponding assistant message
      // We assume grades are given for assistant responses in order
      const correlatedIndex = grade.turn_correlation_id
        ? assistantMessages.findIndex(message => message.turn_correlation_id === grade.turn_correlation_id)
        : -1;
      const targetIndex = correlatedIndex >= 0 ? correlatedIndex : gradeIndex;
      if (targetIndex < assistantMessages.length) {
        const targetMessage = assistantMessages[targetIndex];
        messagesWithGrades[targetMessage.originalIndex] = {
          ...messagesWithGrades[targetMessage.originalIndex],
          grade
        };
      }
    });

    return messagesWithGrades;
  }, []);

  // Get messages with associated grades
  const messagesWithGrades = useMemo(() => {
    return associateGradesWithMessages(chatState.messages, gradeData);
  }, [chatState.messages, gradeData, associateGradesWithMessages]);

  // Suggestion prompts
  const suggestions = useMemo(() => [
    {
      title: 'Extract Information',
      text: 'Extract key information from the attached medical records',
      prompt: `Extract *concise* and *accurate* information from the EHRs related to the {diagnosis codes} and {procedure codes} and generate a structured note using the following format:
  Admitted: [Date/time, reason for admission]
  Diagnosis (Dx): [Primary and secondary diagnoses]
  Assessment: [age and gender presents with]
  History (Hx): [Past medical/surgical history, medications, allergies]
  Labs/Imaging: [Key lab results and imaging findings]
  Plan of Care (POC): [Treatment plan, medications, consults, follow-up]
  Hospitalist History & Physical Exam: [Detailed physical exam findings and hospitalist notes]

*The note should be concise, relevant to the authorization request, and avoid unnecessary details. The answer ***must*** be grounded in the source data.*`,
      primary: true,
      icon: <ClipboardBulletList20Regular />
    },
    {
      title: 'Indications and Contradictions',
      text: 'List Indications or Contradictions for Prior Authorization',
      prompt: `Examine the NCD, LCDs, and BCBSNE Medical policy related to these {procedure codes} and {diagnosis codes}. Your goal is to identify any specific indications or contraindications for the requested services. Output them in the following format:

\`\`\`
[Policy Title] - [Policy Type] [Policy ID]
- **Coverage Criteria:**
  - [Criterion 1] (verbatim from policy)
  - [Criterion 2]
  - [additional criteria...]

- **Covered Indications:**
  - [Indication Description] (verbatim from policy)
  - [Indication Description]
  - [additional indications...]

  - **Contraindications:**
  - [Contraindication Description] (verbatim from policy)
  - [Contraindication Description]
  - [additional contraindications...]

- **Documentation Requirements:**
  - [Requirement 1]
  - [Requirement 2]
  - [additional requirements...]

- **Exclusions / Limitations:**
  - [Exclusion 1]
  - [Exclusion 2]
  - [additional exclusions...]

- **Source:** [Policy Title], [URL if available], effective [date]
\`\`\`

*The result should be relevant to the authorization request. The answer ***must*** be grounded in the source data.*`,
      icon: <Doctor20Regular />
    },
  ], []);

  // Render all messages - no virtualization to prevent content disappearing
  const visibleMessages = useMemo(() => {
    // Create a copy of messages with grades and stable index-based ordering
    const messagesWithIndex = messagesWithGrades.map((msg, originalIndex) => ({
      ...msg,
      originalIndex
    }));
    
    // Sort primarily by timestamp, but ensure user messages come before assistant messages
    // when timestamps are very close (within 5 seconds)
    return messagesWithIndex.sort((a, b) => {
      const timeA = a.timestamp ? new Date(a.timestamp).getTime() : a.originalIndex * 1000;
      const timeB = b.timestamp ? new Date(b.timestamp).getTime() : b.originalIndex * 1000;
      
      const timeDiff = Math.abs(timeA - timeB);
      
      // If timestamps are very close (within 5 seconds), prioritize user messages
      if (timeDiff < 2000) {
        if (a.role === 'user' && b.role === 'assistant') return -1;
        if (a.role === 'assistant' && b.role === 'user') return 1;
      }
      
      // Otherwise sort by timestamp, fallback to original index
      if (timeA !== timeB) return timeA - timeB;
      return a.originalIndex - b.originalIndex;
    });
  }, [messagesWithGrades]);

  // Extract links from conversation when messages change
  useEffect(() => {
    if (chatState.isStreaming) return;

    const timeoutId = setTimeout(() => {
      const allLinks = chatState.messages.flatMap((msg, index) => {
        if (msg.role === 'assistant') {
          return LinkExtractor.extractLinksFromMessage(msg.content, index, new Date().toISOString());
        }
        return [];
      });

      const uniqueLinks = Array.from(
        new Map(allLinks.map(link => [link.url, link])).values()
      );

      onConversationLinksChange(uniqueLinks);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [chatState.messages, chatState.isStreaming, onConversationLinksChange]);

  // Unified scroll position checker
  const checkScrollPosition = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    
    // More forgiving threshold to account for subpixel rendering and padding
    const nearBottom = distanceFromBottom <= 10; // Within 10px or already at bottom
    
    // Additional check: if we're very close to the bottom or already scrolled to the max
    const isAtBottom = Math.abs(scrollTop - (scrollHeight - clientHeight)) <= 10;
    const actuallyNearBottom = nearBottom || isAtBottom;
    
    setIsNearBottom(actuallyNearBottom);
    setShouldAutoScroll(actuallyNearBottom);
    setShowScrollButton(!actuallyNearBottom); // Show button when NOT near bottom
  }, []);

  // Handle scroll position tracking with debouncing for performance
  useEffect(() => {
    // Only set up scroll listener when we're in the main chat interface
    if (isLoadingConversation || (chatState.messages.length === 0 && !chatState.isLoading && !chatState.isStreaming)) {
      return;
    }

    const container = messagesContainerRef.current;
    if (!container) return;

    let scrollTimeout: ReturnType<typeof setTimeout> | null = null;
    
    const handleScroll = () => {
      // Debounce scroll events for better performance
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      
      scrollTimeout = setTimeout(checkScrollPosition, 50); // 50ms debounce
    };

    // Initial check when component mounts or container changes
    const initialTimeout = setTimeout(checkScrollPosition, 500);

    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(initialTimeout);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [chatState.messages.length, isLoadingConversation, chatState.isLoading, chatState.isStreaming]);

  // Check scroll position when messages change OR when loading finishes
  useEffect(() => {
    if (isLoadingConversation) return; // Skip while loading
    
    // Only run this effect when messages actually change, not on every render
    // And only if we don't have user interaction happening
    const timeoutId = setTimeout(() => {
      const container = messagesContainerRef.current;
      if (!container) return;
      
      // Only update if we were already auto-scrolling (user was at bottom)
      // This prevents interfering with user scroll actions
      if (shouldAutoScroll) {
        checkScrollPosition();
      }
    }, 750); // Longer delay for message updates
    
    return () => clearTimeout(timeoutId);
  }, [chatState.messages.length, isLoadingConversation, shouldAutoScroll, checkScrollPosition]);

  // Auto-scroll to bottom for completed messages — NOT during streaming
  useEffect(() => {
    if (shouldAutoScroll && isNearBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatState.messages, statusMessage, shouldAutoScroll, isNearBottom]);
  // Note: streamingMessage intentionally excluded — streaming scroll handled separately below

  // On first streaming chunk: scroll the TOP of the streaming block into view (once per stream).
  // This lets the response grow downward in place rather than dragging the viewport to the bottom.
  useEffect(() => {
    if (streamingMessage && !hasScrolledToStreamStart.current) {
      hasScrolledToStreamStart.current = true;
      // Small delay so the DOM node is mounted before we scroll
      const t = setTimeout(() => {
        streamingStartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 30);
      return () => clearTimeout(t);
    }
    if (!streamingMessage) {
      // Streaming ended — reset flag for next stream
      hasScrolledToStreamStart.current = false;
    }
  }, [streamingMessage]);

  // Reset tool content modal when conversation changes (auth number or thread ID changes)
  useEffect(() => {
    setCurrentToolContent(null);
    setIsToolModalOpen(false);
  }, [chatState.authNumber, chatState.threadId]);

  // Cleanup: Cancel all active retry operations when session context changes
  useEffect(() => {
    const sessionKey = chatState.sessionId;
    
    // Only abort retries if sessionId actually changed (not when threadId changes from None to a value)
    if (sessionKey && currentSessionRef.current && currentSessionRef.current !== sessionKey) {
      console.log('[ChatInterface] Session changed, aborting all active retries');
      activeRetryControllers.current.forEach((controller, key) => {
        console.log(`[ChatInterface] Aborting retry for session: ${key}`);
        controller.abort();
      });
      activeRetryControllers.current.clear();
    }
    
    if (sessionKey) {
      currentSessionRef.current = sessionKey;
    }
    
    // Cleanup on unmount
    return () => {
      console.log('[ChatInterface] Component unmounting, cleaning up active retry operations');
      activeRetryControllers.current.forEach((controller, key) => {
        console.log(`[ChatInterface] Aborting retry for: ${key}`);
        controller.abort();
      });
      activeRetryControllers.current.clear();
    };
  }, [chatState.sessionId, chatState.authNumber]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || chatState.isLoading) return;

    const userMessage: ChatMessage = {
      role: 'user',
      content: inputValue,
      timestamp: new Date().toISOString(),
    };

    // Add user message to state
    onChatStateChange(prev => ({
      ...prev,
      messages: [...prev.messages, userMessage],
      isLoading: true,
      isStreaming: true,
      error: null,
    }));

    onInputValueChange('');
    onStreamingMessageChange('');
    setStreamingThinking('');
    onTodosChange([]); // Clear todos when user sends a new message
    
    // Ensure autoscroll for new messages
    setShouldAutoScroll(true);
    setIsNearBottom(true);

    try {
      // Track accumulated streaming message locally
      let accumulatedMessage = '';
      let capturedToolContent: any[] | null = null;
      let capturedThinkingContent = '';
      
      const response = await ChatAPI.sendMessageStream(
        inputValue,
        chatState.authNumber || undefined,
        chatState.threadId || undefined,
        (chunk: string) => {
          accumulatedMessage += chunk;
          onStreamingMessageChange(accumulatedMessage);
          // Ensure we stay scrolled during streaming if user is near bottom
          if (isNearBottom) {
            setShouldAutoScroll(true);
          }
        },
        (status: string) => {
          onStatusMessageChange(status);
        },
        (gradeData: any) => {
          onGradeReceived?.(gradeData);
        },
        selectedDocuments.length > 0 ? selectedDocuments : undefined,
        (toolContent: any[]) => {
          capturedToolContent = toolContent;
        },
        chatState.sessionId || undefined, // Pass session ID to backend
        uiMode.toLowerCase() === 'appeals' ? 'appeals' : 'authorization', // Map UI mode to backend format
        (thinkingChunk: string) => {
          capturedThinkingContent += thinkingChunk;
          setStreamingThinking(prev => prev + thinkingChunk);
        },
        (incomingTodos) => {
          onTodosChange(incomingTodos);
        }
      );

      // Add assistant response with tool content and thinking if available
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: response.response,
        timestamp: new Date().toISOString(),
        turn_correlation_id: response.turn_id || undefined,
        toolContent: capturedToolContent || undefined,
        thinkingContent: capturedThinkingContent || undefined,
      };

      onChatStateChange(prev => ({
        ...prev,
        messages: [...prev.messages, assistantMessage],
        isLoading: false,
        isStreaming: false,
        threadId: response.thread_id,
      }));

      // Clear streaming state after successful send
      onStreamingMessageChange('');
      onStatusMessageChange('');
      setStreamingThinking('');

      // Refresh grading data after successful completion with retry mechanism
      if (onRefreshGrades && response.is_graded && response.turn_id && response.thread_id && chatState.sessionId) {
        const threadId = response.thread_id;
        const sessionKey = chatState.sessionId;
        const turnCorrelationId = response.turn_id;
        
        // Cancel any existing retry operation for this session to prevent duplicates
        const existingController = activeRetryControllers.current.get(sessionKey);
        if (existingController) {
          console.log('[ChatInterface] Canceling previous grade fetch retry for session:', sessionKey);
          existingController.abort();
          activeRetryControllers.current.delete(sessionKey);
        }

        const controller = retryWithBackoff(
          () => onRefreshGrades(threadId, turnCorrelationId || undefined),
          {
            maxAttempts: 3,
            delays: [10000, 15000],
            shouldRetry: (grades) => {
              // Verify we're still in the same session
              const currentKey = chatState.sessionId;
              if (currentKey && currentKey !== sessionKey) {
                console.log('[ChatInterface] Session changed during retry, aborting');
                return false;
              }
              return Boolean(turnCorrelationId) && !grades.some(
                grade => grade.turn_correlation_id === turnCorrelationId
              );
            },
            onAttempt: (attempt) => {
              console.log(`[ChatInterface] Grade fetch attempt ${attempt} for session:`, sessionKey);
            },
            onSuccess: () => {
              console.log('[ChatInterface] Successfully fetched grades for session:', sessionKey);
              activeRetryControllers.current.delete(sessionKey);
            },
            onFailure: (error) => {
              console.warn('[ChatInterface] Failed to fetch grades after all retries for session:', sessionKey, error);
              activeRetryControllers.current.delete(sessionKey);
            },
          }
        );

        // Store controller with session key for cleanup
        activeRetryControllers.current.set(sessionKey, controller);
      }

    } catch (error) {
      console.error('Failed to send message:', error);
      onChatStateChange(prev => ({
        ...prev,
        isLoading: false,
        isStreaming: false,
        error: error instanceof Error ? error.message : 'Failed to send message',
      }));
      
      // Clear streaming message and status message on error too
      onStreamingMessageChange('');
      onStatusMessageChange('');
      setStreamingThinking('');
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const applySuggestion = (text: string, replace = false) => {
    const base = replace || !inputValue.trim() ? text : `${inputValue.trim()} ${text}`;
    onInputValueChange(base);
  };

  const scrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) return;

    setShouldAutoScroll(true);
    setIsNearBottom(true);
    
    container.scrollTo({
      top: container.scrollHeight,
      behavior: 'smooth'
    });
  };

  const renderMessage = useCallback((message: ChatMessage, index: number) => {
    const isUser = message.role === 'user';
    const hasGrade = !isUser && message.grade;
    
    if (isUser) {
      return (
        <Card
          key={`message-${index}-${message.timestamp}-${message.role}`}
          className={classes.messageCard}
          style={{
            alignSelf: 'flex-end',
            backgroundColor: tokens.colorNeutralBackground1,
            color: tokens.colorNeutralForeground1,
            borderRadius: '12px',
            boxShadow: '0 1px 6px rgba(15,23,42,0.08)',
            border: `1px solid ${tokens.colorNeutralStroke1}`,
            borderLeft: '3px solid #0EA5E9',
          }}
        >
          <CardHeader
            className={classes.messageHeader}
            header={
              <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
                <UserProfile size="small" showFullProfile={false} />
                <Text size={300} weight="semibold" style={{ color: tokens.colorNeutralForeground3, fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>You</Text>
              </div>
            }
          />
          <div className={classes.messageContent}>
            <MessageRenderer content={message.content} isUserMessage={true} />
          </div>
        </Card>
      );
    }

    // Agent response — full-width inline layout (no card chrome)
    return (
      <div
        key={`message-${index}-${message.timestamp}-${message.role}`}
        className={classes.agentMessageWrapper}
      >
        <div className={classes.agentMessageHeader}>
          <div className={classes.agentIcon}>A</div>
          <Text size={300} weight="semibold" className={classes.agentName}>Aurora AI</Text>
        </div>
        <div className={classes.messageContent} style={{ padding: 0 }}>
          {/* Collapsed reasoning block — shown above message content for historical messages */}
          {message.thinkingContent && (
            <ThinkingBlock content={message.thinkingContent} defaultExpanded={false} />
          )}
          <div style={{ marginBottom: hasGrade ? tokens.spacingVerticalXXS : 0 }}>
            <MessageRenderer content={message.content} isUserMessage={false} />
          </div>
          {/* Tool Content Button */}
          {message.toolContent && message.toolContent.length > 0 && (
            <div style={{ marginTop: tokens.spacingVerticalS }}>
              <Button
                appearance="secondary"
                size="small"
                icon={<DocumentMultiple20Regular />}
                onClick={() => {
                  setCurrentToolContent(message.toolContent || null);
                  setIsToolModalOpen(true);
                }}
                style={{
                  fontWeight: 500,
                }}
              >
                View Sources ({message.toolContent.reduce((sum, tool) => sum + tool.chunks.length, 0)} chunks)
              </Button>
            </div>
          )}
          {/* Inline Grade Badge */}
          {hasGrade && (
            <>
              <hr />
              <InlineGradeBadge grade={message.grade!} />
            </>
          )}
        </div>
      </div>
    );
  }, [classes]);

  // Loading overlay
  if (isLoadingConversation) {
    return (
      <div className={classes.container}>
        <div className={classes.loadingOverlay}>
          <Spinner size="large" />
          <Text size={400}>Loading conversation...</Text>
          {loadingConversationError && (
            <Text style={{ color: tokens.colorPaletteRedForeground1 }}>
              {loadingConversationError}
            </Text>
          )}
        </div>
      </div>
    );
  }

  // Empty state with suggestions
  if (chatState.messages.length === 0 && !chatState.isLoading && !chatState.isStreaming) {
    return (
      <div className={classes.container}>
        <div className={classes.emptyState}>
          <ChatMultiple20Regular style={{ fontSize: '64px', marginBottom: tokens.spacingVerticalL }} />
          <Text size={600} weight="semibold" style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 800, letterSpacing: '-0.03em', color: tokens.colorNeutralForeground1 }}>Welcome to Aurora AI</Text>
          <Text size={400}>
            {chatState.authNumber ? 
              `Ask questions about authorization ${chatState.authNumber} or general medical management topics.` :
              'Enter an authorization number above or ask general medical management questions.'
            }
          </Text>
          
          {/* AI Services Status */}
          <div style={{ marginTop: tokens.spacingVerticalM }}>
            <Badge 
              appearance={chatState.aiServicesAvailable ? 'filled' : 'outline'}
              color={chatState.aiServicesAvailable ? 'success' : 'warning'}
            >
              AI Services: {chatState.aiServicesAvailable ? 'Available' : 'Limited'}
            </Badge>
          </div>

          {/* Suggestion Cards */}
          <div className={classes.suggestionContainer}>
            {suggestions.map((suggestion, index) => (
              <Card 
                key={index}
                className={classes.bubbleCard}
                onClick={() => applySuggestion(suggestion.prompt, true)}
              >
                <div className={classes.bubbleCardTitle}>
                  {suggestion.icon}
                  <Text weight="semibold">{suggestion.title}</Text>
                </div>
                <Text size={300}>{suggestion.text}</Text>
              </Card>
            ))}
          </div>
        </div>

        {/* Todo Panel above input */}
        <TodoPanel todos={todos} />

        {/* Input Area */}
        <div className={classes.inputContainer}>
          <Textarea
            className={classes.inputArea}
            placeholder="Type your medical management question here..."
            value={inputValue}
            onChange={(_, data) => onInputValueChange(data.value)}
            onKeyDown={handleKeyDown}
            disabled={chatState.isLoading}
            style={{ height: '100%', borderRadius: '10px', fontFamily: "'DM Sans', 'Segoe UI', sans-serif", fontSize: '14px' }}
          />
          <Button
            className={classes.sendButton}
            appearance="primary"
            icon={<Send20Regular />}
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || chatState.isLoading}
            title="Send message (Enter)"
          />
        </div>
      </div>
    );
  }

  // Main chat interface
  return (
    <div className={classes.container}>
      {/* Messages */}
      <div 
        className={classes.messagesContainer}
        ref={messagesContainerRef}
      >
        {visibleMessages.map(renderMessage)}
        
        {/* Streaming area — shows thinking block and/or streaming message */}
        {(streamingMessage || streamingThinking) && (
          <div ref={streamingStartRef} className={classes.agentMessageWrapper}>
            <div className={classes.agentMessageHeader}>
              <div className={classes.agentIcon}>A</div>
              <Text size={300} weight="semibold" className={classes.agentName}>Aurora AI</Text>
              {chatState.isStreaming && <Spinner size="tiny" />}
            </div>
            {/* Live thinking/reasoning block — expanded while streaming */}
            {streamingThinking && (
              <ThinkingBlock content={streamingThinking} defaultExpanded={true} label="Thinking..." />
            )}
            {streamingMessage && (
              <div className={classes.messageContent} style={{ padding: 0 }}>
                <MessageRenderer content={streamingMessage} isUserMessage={false} />
              </div>
            )}
          </div>
        )}

        {/* Status message — shown when streaming is in flight but no content yet */}
        {(statusMessage || (chatState.isStreaming && !streamingMessage && !streamingThinking)) && (
          <div className={classes.streamingIndicator}>
            <Spinner size="tiny" />
            <Text>{statusMessage || 'AI is thinking...'}</Text>
          </div>
        )}

        {/* Error message */}
        {chatState.error && (
          <Card style={{ 
            backgroundColor: tokens.colorPaletteRedBackground1,
            border: `1px solid ${tokens.colorPaletteRedBorder1}`,
            padding: tokens.spacingVerticalM,
          }}>
            <Text style={{ color: tokens.colorPaletteRedForeground1 }}>
              Error: {chatState.error}
            </Text>
          </Card>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Scroll to bottom button - floating within chat area but outside messages */}
      {showScrollButton && (
        <Button
          className={classes.scrollToBottomButton}
          appearance="primary"
          icon={<ChevronDown20Regular />}
          onClick={scrollToBottom}
          title="Scroll to bottom"
        />
      )}

      {/* Todo Panel above input */}
      <TodoPanel todos={todos} />

      {/* Input Area */}
      <div className={classes.inputContainer}>
        <Textarea
          className={classes.inputArea}
          placeholder="Type your medical management question here..."
          value={inputValue}
          onChange={(_, data) => onInputValueChange(data.value)}
          onKeyDown={handleKeyDown}
          disabled={chatState.isLoading}
          style={{ height: '100%', borderRadius: '10px', fontFamily: "'Plus Jakarta Sans', 'Segoe UI', sans-serif" }}
        />
        <Button
          className={classes.sendButton}
          appearance="primary"
          icon={<Send20Regular />}
          onClick={handleSendMessage}
          disabled={!inputValue.trim() || chatState.isLoading}
          title="Send message (Enter)"
        />
      </div>
      
      {/* Suggestion bar that appears after conversation starts */}
      {(chatState.messages.length > 0 || chatState.threadId) && (
        <div className={classes.suggestionBar} aria-hidden={false} style={{ maxWidth: '100%' }}>
          {suggestions.map((s, i) => (
            <div
              key={`sugg-main-${i}`}
              className={`${classes.bubble} ${i === 0 ? classes.bubblePrimary : ''} ${classes.bubbleSmall}`}
              style={i === 0 ? { background: '#0EA5E9', color: '#FFFFFF', border: 'none' } : {}}
              onClick={() => applySuggestion(s.prompt, true)}
              onDoubleClick={() => applySuggestion(s.prompt, true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') applySuggestion(s.prompt, true); }}
              title={i === 0 ? 'Primary suggestion - click to insert (double-click to replace)' : 'Click to insert suggestion'}
            >
              <Text size={200} weight={i === 0 ? 'semibold' : 'regular'}>{s.text}</Text>
            </div>
          ))}
        </div>
      )}

      {/* Tool Content Modal */}
      {currentToolContent && (
        <ToolContentModal
          isOpen={isToolModalOpen}
          onClose={() => {
            setIsToolModalOpen(false);
            setCurrentToolContent(null);
          }}
          toolContent={currentToolContent}
        />
      )}
    </div>
  );
};

export default ChatInterface;